import {
  ExtranetCustomerGroupDto,
  ExtranetCustomerGroupPouchLayoutDto,
  ExtranetCustomerGroupScheduleSlotDto,
} from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  ExtranetContainer,
  ExtranetEditButton,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacBadge, PharmacIcon, PharmacText, PharmacTooltip } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { isWeekdaySunday, weekdayShortTranslations } from './util/date-utils'

type Props = {
  customerGroup: ExtranetCustomerGroupDto
  schedule: ExtranetCustomerGroupScheduleSlotDto | null
  pouchLayoutId: number | null
}

export const CustomerGroupsListItemMobileDetails = ({
  customerGroup,
  schedule,
  pouchLayoutId,
}: Props) => {
  const navigate = useNavigate()
  const createLink = useExtranetPath()
  const { key, t } = useLanguage()
  const { maxMobile, minDesktop } = useBreakpoint()
  const { customer } = useCustomerContext()

  const getCustomerGroupPouch = (
    customerId?: number
  ): UseQueryOptions<Array<ExtranetCustomerGroupPouchLayoutDto>> => ({
    queryFn: async () => await api.customers.getCustomerGroupPouchLayout(customerId as number),
    queryKey: ['__customerGroupPouch', customer?.id],
    enabled: typeof customerId === 'number',
  })

  const { data: customerGroupPouch } = useQuery(getCustomerGroupPouch(customer?.id))

  const layout = customerGroupPouch?.find((pouch) => pouch.id === Number(pouchLayoutId))

  return (
    <ExtranetContainer
      style={{ borderTop: '2px dashed var(--color-accent-4)' }}
      full
      flexRow
      paddingMedium
    >
      <ExtranetContainer
        twoThirds
        flexColumn
      >
        <PharmacText weight="bold">{t('extranet:customerGroupSettings.orderTime')}</PharmacText>

        <ExtranetContainer
          flexRow
          alignCenter
          paddingSmallTop
          paddingLargeBottom
        >
          {customerGroup.scheduleSlot === null ? (
            <>
              <PharmacIcon
                icon="xCircle"
                stroke="red"
                size="20px"
              />
              <PharmacText marginSmallLeft>
                {t('extranet:customerGroupSettings.scheduleNotSet')}
              </PharmacText>
            </>
          ) : (
            <>
              {schedule?.cycleDateIndex !== undefined ? (
                <>
                  {isWeekdaySunday(schedule.cycleDateIndex) ? (
                    <>
                      <PharmacBadge
                        variant="primary"
                        marginSmallRight
                      >
                        <PharmacText paddingSmallHorizontal>{t('common:week')} 1</PharmacText>
                      </PharmacBadge>
                      <PharmacText>
                        {t(weekdayShortTranslations(schedule?.deadlineWeekday) as any)}{' '}
                        {schedule?.deadlineTime}
                      </PharmacText>
                    </>
                  ) : (
                    <>
                      <PharmacBadge
                        variant="secondary"
                        marginSmallRight
                      >
                        <PharmacText paddingSmallHorizontal>{t('common:week')} 2</PharmacText>
                      </PharmacBadge>
                      <PharmacText>
                        {t(weekdayShortTranslations(schedule?.deadlineWeekday) as any)}{' '}
                        {schedule?.deadlineTime}
                      </PharmacText>
                    </>
                  )}
                </>
              ) : (
                <>-</>
              )}
            </>
          )}
        </ExtranetContainer>
        <ExtranetContainer
          full
          flexRow
          alignCenter
        >
          <PharmacText weight="bold">
            {t('extranet:customerGroupSettings.nextOrderDeadline')}
          </PharmacText>
          <PharmacTooltip
            paddingSmallLeft
            maxWidth={!minDesktop ? '295px' : ''}
            position="top"
            handle={
              <PharmacIcon
                style={{ color: 'var(--color-info)' }}
                size="20px"
                icon="info"
              />
            }
          >
            <PharmacText>
              {t('extranet:customerGroupSettings.nextOrderDeadlineTooltip')}
            </PharmacText>
          </PharmacTooltip>
        </ExtranetContainer>
        <PharmacText
          paddingSmallTop
          paddingLargeBottom
        >
          {customerGroup.nextOrderTime ? (
            <PharmacText>{formatDateTimeString(customerGroup.nextOrderTime)}</PharmacText>
          ) : (
            <ExtranetContainer flexRow>
              <PharmacIcon
                icon="xCircle"
                stroke="red"
                size="20px"
              />

              <PharmacText marginSmallLeft>
                {t('extranet:customerGroupSettings.nextOrderTime.notSet')}
              </PharmacText>
            </ExtranetContainer>
          )}
        </PharmacText>
        <PharmacText weight="bold">
          {t('extranet:customerGroupSettings.doseDispensingFee')}
        </PharmacText>
        <ExtranetContainer
          flexRow
          alignCenter
          paddingSmallTop={!customerGroup?.pricingProductId}
          paddingLargeBottom={!customerGroup?.pricingProductId}
        >
          {customerGroup?.pricingProductId ? (
            <PharmacText
              paddingSmallTop
              paddingLargeBottom
            >
              {key(customerGroup?.productName)}
            </PharmacText>
          ) : (
            <>
              <PharmacIcon
                icon="xCircle"
                stroke="red"
                size="20px"
              />

              <PharmacText marginSmallLeft>
                {t('extranet:customerGroupSettings.notSet')}
              </PharmacText>
            </>
          )}
        </ExtranetContainer>

        <ExtranetContainer
          full
          flexRow
          alignCenter
          paddingSmallBottom
        >
          <PharmacText weight="bold">{t('extranet:customerGroupSettings.activity')}</PharmacText>
          <PharmacTooltip
            paddingSmallLeft
            maxWidth={maxMobile ? '295px' : ''}
            position="top"
            handle={
              <PharmacIcon
                style={{ color: 'var(--color-info)' }}
                size="20px"
                icon="info"
              />
            }
          >
            <PharmacText>{t('extranet:customerGroupSettings.activityTooltip')}</PharmacText>
          </PharmacTooltip>
        </ExtranetContainer>

        <ExtranetContainer
          flexRow
          alignCenter
          paddingLargeBottom
        >
          {customerGroup.active ? (
            <ExtranetContainer
              flexRow
              alignCenter
            >
              <CheckIcon icon="checkCircle" />
              <PharmacText>{t('common:active')}</PharmacText>
            </ExtranetContainer>
          ) : (
            <ExtranetContainer
              flexRow
              alignCenter
            >
              <PharmacIcon
                icon="xCircle"
                stroke="lightGrey"
              />
              <PharmacText marginSmallLeft>{t('common:passive')}</PharmacText>
            </ExtranetContainer>
          )}
        </ExtranetContainer>

        <PharmacText weight="bold">{t('extranet:customerGroupSettings.disclosure')}</PharmacText>
        {customerGroup.dataReleaseConsents.length > 0 ? (
          <>
            {customerGroup.dataReleaseConsents?.map((a) => (
              <PharmacText paddingSmallTop>{a.name}</PharmacText>
            ))}
          </>
        ) : (
          <PharmacText paddingSmallTop>
            {t('extranet:customerGroupSettings.dataRelease')}
          </PharmacText>
        )}
        <PharmacText
          weight="bold"
          paddingLargeTop
        >
          {t('extranet:customerGroupSettings.doseBagLayout')}
        </PharmacText>
        {layout ? (
          <PharmacText
            paddingSmallTop
            paddingLargeBottom
          >
            {layout?.name}
          </PharmacText>
        ) : (
          <PharmacText
            paddingSmallTop
            paddingLargeBottom
          >
            {t('extranet:customerGroupSettings.normalLayout')}
          </PharmacText>
        )}

        <PharmacText
          weight="bold"
          paddingSmallBottom
        >
          {t('extranet:customerGroupSettings.stickerlessGroup')}
        </PharmacText>
        <ExtranetContainer
          flexRow
          alignCenter
          paddingLargeBottom
        >
          {customerGroup.omitStickers && <CheckIcon icon="checkCircle" />}
        </ExtranetContainer>
      </ExtranetContainer>
      <ExtranetContainer
        alignEnd
        justifyEnd
        flexRow
        paddingSmall
        third
      >
        <ExtranetEditButton
          onClick={() =>
            navigate(
              createLink('oma-annos.customerGroupManagement.edit', { id: customerGroup.groupId })
            )
          }
        />
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
